import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import { Button } from "../components/ui/button";
import EncomendaForm from "../components/EncomendaForm";
import { Package, Plus, TrendingUp, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Encomendas() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/encomendas");
      setItems(data || []);
    } catch { toast.error("Erro ao carregar encomendas"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const totals = items.reduce(
    (a, e) => ({
      invested: a.invested + (e.summary?.total_invested || 0),
      realized: a.realized + (e.summary?.profit_realized || 0),
      potential: a.potential + (e.summary?.potential_profit || 0),
    }),
    { invested: 0, realized: 0, potential: 0 },
  );

  return (
    <div className="min-h-screen bg-background" data-testid="encomendas-page">
      <DashboardHeader title="Encomendas" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 lb-fade-in">
          <div>
            <div className="lb-overline">Compras ao fornecedor</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-1">Encomendas</h1>
            <p className="text-sm text-muted-foreground mt-1">Regista cada remessa recebida — o stock atualiza automaticamente e o lucro é rastreado por lote (FIFO).</p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline"><Link to="/dashboard/lista-compras" data-testid="link-shopping-list">Lista de compras</Link></Button>
            <Button onClick={() => setFormOpen(true)} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white" data-testid="new-encomenda-btn">
              <Plus className="w-4 h-4 mr-1" /> Nova encomenda
            </Button>
          </div>
        </div>

        {!loading && items.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <MiniKpi label="Total investido" value={format(totals.invested)} />
            <MiniKpi label="Lucro realizado" value={format(totals.realized)} accent />
            <MiniKpi label="Lucro potencial" value={format(totals.potential)} />
          </div>
        )}

        {loading ? (
          <div className="space-y-3">{Array.from({length:3}).map((_,i)=><div key={i} className="h-24 bg-white rounded-2xl lb-shadow-card animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl lb-shadow-card" data-testid="empty-encomendas">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <h3 className="mt-3 font-display text-xl">Ainda sem encomendas registadas</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Cria a primeira encomenda ou constrói uma lista de compras que depois converte numa encomenda.</p>
            <div className="mt-4 flex gap-2 justify-center">
              <Button onClick={() => setFormOpen(true)} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58]"><Plus className="w-4 h-4 mr-1" /> Nova encomenda</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((e) => (
              <Link
                key={e.id}
                to={`/dashboard/encomendas/${e.id}`}
                className="block bg-white rounded-2xl lb-shadow-card p-4 lb-hover-lift"
                data-testid={`encomenda-row-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="lb-overline">{new Date(e.received_at || e.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    <div className="font-display text-lg truncate">{e.supplier || "Sem fornecedor"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {e.items?.length || 0} produto(s) · {e.summary?.total_qty || 0} unidade(s) · vendido {e.summary?.sell_through_pct || 0}%
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="lb-overline">Investido</div>
                    <div className="font-display text-lg">{format(e.summary?.total_invested || 0)}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground/40 shrink-0 mt-1" />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">Lucro realizado</div>
                    <div className="font-semibold text-[hsl(var(--primary))]">{format(e.summary?.profit_realized || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Lucro potencial</div>
                    <div className="font-semibold">{format(e.summary?.potential_profit || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Restam</div>
                    <div className="font-semibold">{e.summary?.remaining_qty || 0} un.</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${Math.min(100, e.summary?.sell_through_pct || 0)}%` }} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <EncomendaForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
    </div>
  );
}

function MiniKpi({ label, value, accent }) {
  return (
    <div className="rounded-2xl p-3 bg-white lb-shadow-card">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <TrendingUp className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-1 font-display text-xl ${accent ? "text-[hsl(var(--primary))]" : ""}`}>{value}</div>
    </div>
  );
}
